import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiService } from '../../../services/api.service';

export type RbacBindingKind = 'route' | 'action' | 'feature';

export interface IRbacRole {
  _id?: string;
  role: string;
  name: string;
}

export interface IRbacPermission {
  _id?: string;
  key: string;
  app_type: string;
  subtype?: string;
  name: string;
  description?: string;
  roles_allow: string;
  /** Client-only: shown from a parent layer (no own row at the viewed scope). */
  inherited?: boolean;
  /** Client-only: which layer supplies the value (`Global`, `sport`, `sport/club`). */
  via?: string;
}

export interface IRbacTenantOverride {
  _id: string;
  company: string;
  permission_key: string;
  role: string;
  allow: boolean;
}

export interface IRbacTenantOverrideSaveResult {
  override?: IRbacTenantOverride;
  applied: boolean;
  skipped: string[];
  error?: string;
}

export interface IGlobalChange {
  key: string;
  name: string;
  roles_allow: string;
  previous_roles: string | null;
  created: boolean;
  changed_at?: string;
}

export interface IGlobalChangesResult {
  last_push_at: string | null;
  modified: IGlobalChange[];
}

export interface IGlobalPushResult {
  dry_run: boolean;
  modified: { key: string; name: string }[];
  app_types: {
    app_type: string;
    targets: number;
    ok: number;
    failed: number;
    results: { company: string; domain?: string; ok: boolean; error?: string }[];
  }[];
  targets: number;
  ok: number;
  failed: number;
  recorded: boolean;
}

export interface ITenantPending {
  company: string;
  domain?: string;
  app_type: string;
  subtype: string;
  last_received_at: string | null;
  pending: string[];
}

export interface ITenantPushResult {
  dry_run: boolean;
  targets: number;
  ok: number;
  failed: number;
  pairs: number;
  results: { company: string; domain?: string; app_type: string; keys: string[]; ok: boolean; error?: string }[];
}

export interface IRbacBinding {
  _id?: string;
  app_type: string;
  subtype?: string;
  kind: RbacBindingKind;
  target: string;
  permission_key: string;
  label?: string;
  inherited?: boolean;
  via?: string;
}

export interface IRbacAcl {
  _id?: string;
  acl: string;
  roles_short: string;
  roles_id: string;
  app_type?: string;
  subtype?: string;
  inherited?: boolean;
  via?: string;
}

export interface ITypedCopiesResult {
  dry_run: boolean;
  counts: { permission: number; binding: number; acl: number };
  total: number;
  sample: { entity: string; app_type: string; subtype: string; id: string }[];
}

export interface ISetPermissionRolesResult {
  action: 'update-global' | 'upsert' | 'delete' | 'noop';
  roles_allow: string;
  row: IRbacPermission | null;
}

export type RbacResourceSubjectType = 'role' | 'user';

export interface IRbacResourceGrant {
  _id?: string;
  app_type: string;
  subtype?: string;
  resource_kind: string;
  resource_id: string;
  subject_type: RbacResourceSubjectType;
  subject: string;
  permission_key: string;
  label?: string;
}

export interface IEffectiveScopedRbac {
  app_type: string;
  subtype?: string;
  roles: IRbacRole[];
  acls: IRbacAcl[];
  permissions: IRbacPermission[];
  bindings: IRbacBinding[];
  /** Grants matching session user id or role */
  resource_grants?: IRbacResourceGrant[];
  /** Documents that have any grant: `kind::id` */
  resource_locked?: string[];
}

export interface ISeedCatalogResult {
  company: string;
  appType?: string | null;
  subtype?: string | null;
  permissionsUpserted: number;
  bindingsUpserted: number;
  rolesUpserted?: number;
  rolesFixed?: number;
  aclsNormalized?: number;
  typedScaffolded?: boolean;
  subtypeScaffolded?: boolean;
  passengerBindingUpserted?: boolean;
  subtypeBackfilled?: number;
  seeded?: boolean;
}

export interface IScopedRbacCatalogStatus {
  company: string;
  is_ssot: boolean;
  seeded: boolean;
  seeded_at?: string | null;
  catalog_revision?: number;
  hub_revision?: number;
  hub_revision_at_push?: number;
  drift?: boolean;
  last_pushed_at?: string | null;
  permission_count?: number;
  can_seed: boolean;
  can_edit_catalog: boolean;
}

export interface IEnsureTypedMastersResult {
  company: string;
  app_types: string[];
  pack_star_upserted?: number;
  results: {
    app_type: string;
    permissionsCloned: number;
    bindingsCloned: number;
    aclsCloned: number;
  }[];
  totals: {
    permissionsCloned: number;
    bindingsCloned: number;
    aclsCloned: number;
  };
}

export interface IRbacAuditRow {
  _id?: string;
  actor_id?: string;
  actor_role?: string;
  actor_label?: string;
  action: string;
  entity: string;
  entity_id?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  createdAt?: string;
}

export interface ISeedFanoutResultItem {
  company: string;
  domain?: string;
  appType: string;
  subtype: string;
  ok: boolean;
  dry_run?: boolean;
  error?: string;
  counts?: ISeedCatalogResult;
}

export interface ISeedFanoutResult {
  source?: 'live' | 'pack';
  app_type: string;
  subtype: string;
  dry_run: boolean;
  targets: number;
  ok: number;
  failed: number;
  results: ISeedFanoutResultItem[];
}

@Injectable({
  providedIn: 'root'
})
export class ScopedRbacService {
  /** Tenant API root ending before resource path (…/api/:company) */
  private get companyRoot(): string {
    const raw = environment.APPTYPES_APIURL.startsWith('http://') || environment.APPTYPES_APIURL.startsWith('https://')
      ? environment.APPTYPES_APIURL
      : environment.BASEURL + environment.APPTYPES_APIURL;
    return raw.replace(/\/apptypes\/?$/, '');
  }

  constructor(private apiService: ApiService) {}

  getEffective(appType: string, subtype: string = '*'): Observable<IEffectiveScopedRbac> {
    const params = [
      `app_type=${encodeURIComponent(appType)}`,
      `subtype=${encodeURIComponent(subtype || '*')}`
    ];
    return this.apiService.get(`${this.companyRoot}/scoped-rbac/effective?${params.join('&')}`);
  }

  getRoles(): Observable<IRbacRole[]> {
    return this.apiService.get(`${this.companyRoot}/roles`);
  }

  createRole(role: IRbacRole): Observable<IRbacRole> {
    return this.apiService.getPost(`${this.companyRoot}/roles`, role);
  }

  updateRole(id: string, role: Partial<IRbacRole>): Observable<IRbacRole> {
    return this.apiService.put(`${this.companyRoot}/roles/${id}`, role);
  }

  deleteRole(id: string): Observable<any> {
    return this.apiService.delete(`${this.companyRoot}/roles/${id}`, { id });
  }

  getPermissions(appType?: string, subtype?: string): Observable<IRbacPermission[]> {
    const params: string[] = [];
    if (appType) params.push(`app_type=${encodeURIComponent(appType)}`);
    if (subtype) params.push(`subtype=${encodeURIComponent(subtype)}`);
    const q = params.length ? `?${params.join('&')}` : '';
    return this.apiService.get(`${this.companyRoot}/permissions${q}`);
  }

  createPermission(permission: IRbacPermission): Observable<IRbacPermission> {
    return this.apiService.getPost(`${this.companyRoot}/permissions`, permission);
  }

  updatePermission(id: string, permission: Partial<IRbacPermission>): Observable<IRbacPermission> {
    return this.apiService.put(`${this.companyRoot}/permissions/${id}`, permission);
  }

  deletePermission(id: string): Observable<any> {
    return this.apiService.delete(`${this.companyRoot}/permissions/${id}`, { id });
  }

  getBindings(appType?: string, kind?: RbacBindingKind, subtype?: string): Observable<IRbacBinding[]> {
    const params: string[] = [];
    if (appType) params.push(`app_type=${encodeURIComponent(appType)}`);
    if (subtype) params.push(`subtype=${encodeURIComponent(subtype)}`);
    if (kind) params.push(`kind=${encodeURIComponent(kind)}`);
    const q = params.length ? `?${params.join('&')}` : '';
    return this.apiService.get(`${this.companyRoot}/rbac-bindings${q}`);
  }

  createBinding(binding: IRbacBinding): Observable<IRbacBinding> {
    return this.apiService.getPost(`${this.companyRoot}/rbac-bindings`, binding);
  }

  updateBinding(id: string, binding: Partial<IRbacBinding>): Observable<IRbacBinding> {
    return this.apiService.put(`${this.companyRoot}/rbac-bindings/${id}`, binding);
  }

  deleteBinding(id: string): Observable<any> {
    return this.apiService.delete(`${this.companyRoot}/rbac-bindings/${id}`, { id });
  }

  getAcls(appType?: string, subtype?: string): Observable<IRbacAcl[]> {
    const params: string[] = [];
    if (appType) params.push(`app_type=${encodeURIComponent(appType)}`);
    if (subtype) params.push(`subtype=${encodeURIComponent(subtype)}`);
    const q = params.length ? `?${params.join('&')}` : '';
    return this.apiService.get(`${this.companyRoot}/acls${q}`);
  }

  createAcl(acl: IRbacAcl): Observable<IRbacAcl> {
    return this.apiService.getPost(`${this.companyRoot}/acls`, acl);
  }

  updateAcl(id: string, acl: Partial<IRbacAcl>): Observable<IRbacAcl> {
    return this.apiService.put(`${this.companyRoot}/acls/${id}`, acl);
  }

  deleteAcl(id: string): Observable<any> {
    return this.apiService.delete(`${this.companyRoot}/acls/${id}`, { id });
  }

  getResourceGrants(opts?: {
    appType?: string;
    subtype?: string;
    resourceKind?: string;
    resourceId?: string;
  }): Observable<IRbacResourceGrant[]> {
    const params: string[] = [];
    if (opts?.appType) params.push(`app_type=${encodeURIComponent(opts.appType)}`);
    if (opts?.subtype) params.push(`subtype=${encodeURIComponent(opts.subtype)}`);
    if (opts?.resourceKind) params.push(`resource_kind=${encodeURIComponent(opts.resourceKind)}`);
    if (opts?.resourceId) params.push(`resource_id=${encodeURIComponent(opts.resourceId)}`);
    const q = params.length ? `?${params.join('&')}` : '';
    return this.apiService.get(`${this.companyRoot}/rbac-resource-grants${q}`);
  }

  createResourceGrant(grant: IRbacResourceGrant): Observable<IRbacResourceGrant> {
    return this.apiService.getPost(`${this.companyRoot}/rbac-resource-grants`, grant);
  }

  updateResourceGrant(id: string, grant: Partial<IRbacResourceGrant>): Observable<IRbacResourceGrant> {
    return this.apiService.put(`${this.companyRoot}/rbac-resource-grants/${id}`, grant);
  }

  deleteResourceGrant(id: string): Observable<any> {
    return this.apiService.delete(`${this.companyRoot}/rbac-resource-grants/${id}`, { id });
  }

  seedCatalog(body: {
    app_type?: string;
    subtype?: string;
    passenger_slug?: string;
  } = {}): Observable<ISeedCatalogResult> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/seed`, body);
  }

  getCatalogStatus(): Observable<IScopedRbacCatalogStatus> {
    return this.apiService.get(`${this.companyRoot}/scoped-rbac/catalog-status`);
  }

  setPermissionRoles(body: {
    key: string;
    app_type: string;
    subtype: string;
    roles_allow: string;
  }): Observable<ISetPermissionRolesResult> {
    return this.apiService.put(`${this.companyRoot}/scoped-rbac/permission-roles`, body);
  }

  pruneTypedCopies(dryRun: boolean, appType?: string): Observable<ITypedCopiesResult> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/prune-typed-copies`, {
      dry_run: dryRun,
      app_type: appType
    });
  }

  resetTenantPermission(company: string, key: string): Observable<{ deleted: number; applied: boolean; error?: string }> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/tenant-overrides/reset`, {
      target_company: company,
      permission_key: key
    });
  }

  getTenantPending(): Observable<{ tenants: ITenantPending[] }> {
    return this.apiService.get(`${this.companyRoot}/scoped-rbac/tenant-pending`);
  }

  pushPending(dryRun: boolean, companies?: string[]): Observable<ITenantPushResult> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/push-pending`, { dry_run: dryRun, companies });
  }

  getGlobalChanges(): Observable<IGlobalChangesResult> {
    return this.apiService.get(`${this.companyRoot}/scoped-rbac/global-changes`);
  }

  pushGlobal(dryRun: boolean): Observable<IGlobalPushResult> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/push-global`, { dry_run: dryRun });
  }

  listTenantOverrides(): Observable<IRbacTenantOverride[]> {
    return this.apiService.get(`${this.companyRoot}/scoped-rbac/tenant-overrides`);
  }

  setTenantOverride(body: {
    target_company: string;
    permission_key: string;
    role: string;
    allow: boolean;
  }): Observable<IRbacTenantOverrideSaveResult> {
    return this.apiService.put(`${this.companyRoot}/scoped-rbac/tenant-overrides`, body);
  }

  deleteTenantOverride(id: string): Observable<IRbacTenantOverrideSaveResult & { message: string }> {
    return this.apiService.delete(`${this.companyRoot}/scoped-rbac/tenant-overrides/${id}`, { id });
  }

  getAudits(limit = 40): Observable<{ audits: IRbacAuditRow[] }> {
    return this.apiService.get(
      `${this.companyRoot}/scoped-rbac/audits?limit=${encodeURIComponent(String(limit))}`
    );
  }

  ensureTypedMasters(body: { app_type?: string } = {}): Observable<IEnsureTypedMastersResult> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/ensure-typed-masters`, body);
  }

  seedTenant(body: {
    target_company: string;
    app_type: string;
    subtype?: string;
    passenger_slug?: string;
    source?: 'live' | 'pack';
  }): Observable<ISeedCatalogResult & { source?: string; aclsUpserted?: number }> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/seed-tenant`, body);
  }

  seedFanout(body: {
    app_type: string;
    subtype?: string;
    dry_run?: boolean;
    companies?: string[];
    source?: 'live' | 'pack';
  }): Observable<ISeedFanoutResult> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/seed-fanout`, body);
  }

  exportRbac(appType: string, entities: string[]): Observable<IScopedRbacExportPayload> {
    const params = [
      `app_type=${encodeURIComponent(appType || '*')}`,
      `entities=${encodeURIComponent(entities.join(','))}`
    ];
    return this.apiService.get(`${this.companyRoot}/scoped-rbac/export?${params.join('&')}`);
  }

  importRbac(body: {
    payload: IScopedRbacExportPayload;
    mode: 'merge' | 'replace';
    entities: string[];
    user_subject_map?: Record<string, string>;
  }): Observable<IScopedRbacImportResult> {
    return this.apiService.getPost(`${this.companyRoot}/scoped-rbac/import`, body);
  }

  resolveUsersForRbac(queries: string[]): Observable<{ results: IRbacUserResolveResult[] }> {
    return this.apiService.getPost(`${this.companyRoot}/users/resolve-for-rbac`, { queries });
  }
}

export type RbacExportEntity =
  | 'roles'
  | 'permissions'
  | 'bindings'
  | 'acls'
  | 'resource_grants';

export interface IScopedRbacExportPayload {
  format: string;
  version: number;
  exported_at: string;
  source_company: string;
  app_type_filter: string;
  entities: RbacExportEntity[];
  roles: IRbacRole[];
  permissions: IRbacPermission[];
  bindings: IRbacBinding[];
  acls: IRbacAcl[];
  resource_grants: Array<
    IRbacResourceGrant & { subject_email?: string; subject_username?: string }
  >;
}

export interface IScopedRbacImportResult {
  mode: 'merge' | 'replace';
  app_type_filter: string;
  entities: string[];
  rolesUpserted: number;
  permissionsUpserted: number;
  bindingsUpserted: number;
  aclsUpserted: number;
  grantsUpserted: number;
  grantsSkippedUnmapped: number;
  grantsSkippedMissingResource: number;
  grantsSkippedMissingResourceSample: string[];
  deleted: {
    roles: number;
    permissions: number;
    bindings: number;
    acls: number;
    resource_grants: number;
  };
}

export interface IRbacUserResolveResult {
  query: string;
  found: boolean;
  user_id?: string;
  email?: string;
  username?: string;
}
